import React from 'react';
import { Link } from 'react-router-dom';
import PublicPageLayout from '../components/landing-page/PublicPageLayout';

export default function TermsOfServicePage() {
  return (
    <PublicPageLayout
      title='Terms of Service'
      subtitle='Terms that govern your use of PricingTable AI websites, software, APIs, and related services.'
    >
      <p className='text-sm text-gray-400'>Last updated: September 21, 2025</p>

      <p className='text-sm text-gray-300'>
       
      </p>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>1. Agreement to terms</h2>
        <p>
          These Terms of Service (&quot;<span className='font-medium text-white'>Terms</span>&quot;) form a binding agreement between you
          and PricingTable AI (&quot;<span className='font-medium text-white'>we</span>,&quot; &quot;
          <span className='font-medium text-white'>us</span>,&quot; or &quot;<span className='font-medium text-white'>our</span>&quot;)
          regarding your access to and use of our websites, hosted application, APIs, installation code, documentation, and related
          offerings (collectively, the &quot;<span className='font-medium text-white'>Services</span>&quot;).
        </p>
        <p>
          By registering for an account, subscribing, accessing the Services, or deploying our installation code or integrations on
          properties you control, you agree to these Terms. If you use the Services on behalf of a company or other entity, you
          represent that you have authority to bind that entity, and &quot;you&quot; includes that entity.
        </p>
        <p>
          Our <Link to='/privacy' className='text-yellow-400 underline hover:text-yellow-300'>Privacy Policy</Link> and{' '}
          <Link to='/cookies' className='text-yellow-400 underline hover:text-yellow-300'>Cookie Policy</Link> describe how we handle
          personal data and cookies on our own sites. Your use of the Services is also subject to those policies where they apply.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>2. Definitions</h2>
        <ul className='list-disc space-y-2 pl-6'>
          <li>
            <span className='font-medium text-white'>Content:</span> materials you or your users upload, transmit, or make
            available through the Services, including pricing table markup (HTML/CSS/JS), configuration data, and experiment
            definitions.
          </li>
          <li>
            <span className='font-medium text-white'>User:</span> an individual you authorize to access your account (for example,
            employees or contractors).
          </li>
          <li>
            <span className='font-medium text-white'>End Users:</span> visitors to your websites or applications who interact with
            pricing experiences delivered through the Services (for example, via our installation code or API).
          </li>
          <li>
            <span className='font-medium text-white'>Documentation:</span> guides, API references, and technical materials we make
            available for the Services.
          </li>
        </ul>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>3. Changes to the Terms or Services</h2>
        <p>
          We may update these Terms or the Services from time to time. When we make material changes, we will provide notice by
          posting an updated version on this page, emailing the address associated with your account, or showing an in-product
          notice, as appropriate. The &quot;Last updated&quot; date above will change when we revise these Terms.
        </p>
        <p>
          If you continue to use the Services after changes become effective, you agree to the revised Terms. If you do not agree,
          you must stop using the Services and cancel your subscription according to our then-current cancellation process.
        </p>
        <p>
          We may also modify, add, or discontinue features to improve security, performance, or the product roadmap, consistent with
          our commitments in any order form or enterprise agreement you may have with us.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>4. Eligibility and accounts</h2>
        <p>
          The Services are intended for business use. You must be at least the age of majority in your jurisdiction and able to form
          a binding contract. You agree to provide accurate registration information and to keep it current.
        </p>
        <p>
          You are responsible for maintaining the confidentiality of credentials and for all activity under your account. You will
          promptly notify us of any unauthorized access or security incident related to your account. We are not liable for losses
          arising from your failure to safeguard credentials.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>5. License to use the Services</h2>
        <p>
          Subject to these Terms and your payment of applicable fees, we grant you a limited, non-exclusive, non-transferable,
          revocable right to access and use the Services during your subscription term, solely for your internal business purposes
          and in accordance with the Documentation.
        </p>
        <p>You will not, and will not permit others to:</p>
        <ul className='list-disc space-y-2 pl-6'>
          <li>Copy, modify, or create derivative works of the Services except as allowed in writing or by applicable law</li>
          <li>Reverse engineer, decompile, or attempt to extract source code except where prohibited restrictions do not apply</li>
          <li>Rent, lease, sell, resell, or sublicense the Services except as expressly permitted</li>
          <li>Use the Services to build a competing product or service or to benchmark or scrape our systems without permission</li>
          <li>Interfere with or disrupt the integrity or performance of the Services or third-party systems</li>
          <li>Circumvent usage limits, security controls, or access restrictions</li>
        </ul>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>6. Description of the Services</h2>
        <p>
          PricingTable AI provides tools for SaaS and similar businesses to run pricing experiments. Functionality may include,
          without limitation: uploading pricing tables; configuring candidate prices and tiers; generating variations; delivering
          pricing experiences through APIs and installation code; collecting interaction and session-related data for analysis; and
          presenting results to help you evaluate revenue and conversion outcomes.
        </p>
        <p>
          Features may vary by plan. We do not guarantee specific experiment outcomes, statistical significance, or business results.
          You remain responsible for business decisions you make based on data from the Services.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>7. Your Content</h2>
        <p>
          As between you and us, you retain ownership of your Content. You represent and warrant that you have all rights and
          consents necessary to upload your Content and to use the Services as contemplated here, and that your Content does not
          violate law or third-party rights.
        </p>
        <p>
          You grant us a worldwide, royalty-free license to host, reproduce, process, transmit, display, and distribute your Content
          solely as needed to provide, secure, and improve the Services—including generating variations, serving them via API or
          embed, storing experiment configuration, and processing analytics and session data associated with your experiments.
        </p>
        <p>
          You are responsible for backing up your Content. We are not obligated to retain your Content after termination except as
          required by law or stated in a separate data processing or retention schedule.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>8. End Users and your properties</h2>
        <p>
          When you deploy our installation code or connect our APIs on websites or apps you operate, End Users interact with
          experiences we help deliver on your behalf. You—not PricingTable AI—are responsible for your relationship with End Users,
          including product terms, refunds, and customer support for your product.
        </p>
        <p>You agree to:</p>
        <ul className='list-disc space-y-2 pl-6'>
          <li>
            Comply with applicable privacy, marketing, and consumer laws (including notice, consent, and opt-out requirements where
            required)
          </li>
          <li>
            Publish a privacy policy (and cookie notice where applicable) that accurately describes your and our processing in
            connection with those experiences, to the extent required by law
          </li>
          <li>Honor requests from End Users exercising privacy rights where you are legally required to do so</li>
        </ul>
        <p>
          If we receive a request or inquiry from an End User or regulator regarding your use of the Services, we may forward it to
          you and cooperate as required by law.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>9. Fees, billing, and taxes</h2>
        <p>
          Paid plans are billed according to the prices and intervals presented at checkout, in your account, or in an order form.
          You authorize us and our payment processors to charge your payment method for applicable fees. Fees are exclusive of taxes
          unless stated otherwise; you are responsible for any taxes other than taxes based on our net income.
        </p>
        <p>
          Subscriptions may renew automatically for successive periods unless you cancel before the renewal date using the
          mechanism we provide. We may change fees with reasonable advance notice; continued use after the effective date may
          constitute acceptance of new fees for the next renewal period.
        </p>
        <p>
          Late or failed payments may result in suspension of access to the Services until amounts owed are paid. Except where
          required by law, fees are non-refundable.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>10. Prohibited conduct</h2>
        <p>In addition to restrictions elsewhere in these Terms, you agree not to:</p>
        <ul className='list-disc space-y-2 pl-6'>
          <li>Use the Services for unlawful, fraudulent, or harmful purposes</li>
          <li>Upload malware or content designed to disrupt systems or exfiltrate data without authorization</li>
          <li>Harass, abuse, or harm individuals or groups; or transmit illegal, defamatory, or infringing content</li>
          <li>Scrape, crawl, or harvest data from the Services except through documented APIs and within permitted rate limits</li>
          <li>Impersonate any person or entity or misrepresent your affiliation</li>
          <li>Violate export control, sanctions, or other applicable regulatory requirements</li>
          <li>Use the Services in any manner that could damage our reputation, systems, or other customers</li>
        </ul>
        <p>
          We may investigate suspected violations and cooperate with law enforcement. We may suspend or terminate access for conduct
          we reasonably believe violates these Terms or creates risk for the Services or third parties.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>11. Intellectual property</h2>
        <p>
          We and our licensors own all rights in the Services, including software, visual design, trademarks, and Documentation,
          excluding your Content. Except for the limited rights expressly granted in these Terms, no rights are transferred to you.
        </p>
        <p>
          You may not remove or alter proprietary notices. Feedback you voluntarily provide may be used by us without restriction or
          obligation to you, subject to our Privacy Policy where personal data is involved.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>12. Third-party services</h2>
        <p>
          The Services may integrate with or link to third-party services (for example, hosting, analytics, or payment providers).
          Those services are governed by their own terms and privacy policies. We are not responsible for third-party services or
          content, and your use of them is at your own risk.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>13. Data protection</h2>
        <p>
          Where we process personal data on your behalf in connection with the Services, our respective roles (controller vs.
          processor), processing details, and safeguards may be further described in our Privacy Policy, a data processing addendum,
          or an enterprise agreement, as applicable.
        </p>
        <p>
          Each party will comply with applicable data protection laws that apply to its role. You will not instruct us to process
          personal data in a way that violates law.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>14. Confidentiality</h2>
        <p>
          If either party receives non-public information from the other that is marked confidential or that reasonably should be
          understood as confidential, the receiving party will use reasonable care to protect it and use it only for the purpose of
          providing or receiving the Services, subject to standard exceptions (public domain, rightfully received from a third party,
          independently developed, required by law).
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>15. Disclaimers</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICES ARE PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot; WE DISCLAIM
          ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
          PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR
          FREE OF HARMFUL COMPONENTS, OR THAT EXPERIMENT RESULTS WILL BE ACCURATE OR SUFFICIENT FOR YOUR COMMERCIAL DECISIONS.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>16. Limitation of liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PRICINGTABLE AI NOR ITS SUPPLIERS WILL BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR LOSS OF PROFITS, REVENUE, GOODWILL, DATA, OR
          BUSINESS OPPORTUNITIES, ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICES, WHETHER BASED ON WARRANTY, CONTRACT,
          TORT (INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p>
          OUR AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE SERVICES OR THESE TERMS WILL NOT EXCEED THE GREATER
          OF (A) THE AMOUNTS YOU PAID US FOR THE SERVICES IN THE TWELVE (12) MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR (B)
          ONE HUNDRED U.S. DOLLARS (US$100) IF NO FEES WERE PAID IN THAT PERIOD.
        </p>
        <p>
          THE LIMITATIONS IN THIS SECTION DO NOT APPLY WHERE PROHIBITED BY LAW (FOR EXAMPLE, LIABILITY FOR DEATH OR PERSONAL INJURY
          CAUSED BY NEGLIGENCE WHERE SUCH LIMITS ARE VOID).
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>17. Indemnification</h2>
        <p>
          You will defend, indemnify, and hold harmless PricingTable AI and its affiliates, officers, directors, employees, and
          agents from and against any third-party claims, damages, losses, and expenses (including reasonable attorneys&apos; fees)
          arising out of or related to: (a) your Content; (b) your use of the Services in violation of these Terms or law; (c)
          your relationship with End Users; or (d) a dispute between you and your customers.
        </p>
        <p>
          We may assume exclusive defense and control of any matter subject to indemnification, at your expense, and you will
          cooperate with our reasonable requests.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>18. Term and termination</h2>
        <p>
          These Terms remain in effect while you use the Services. You may terminate your account according to the cancellation
          process we provide. We may suspend or terminate access if you materially breach these Terms, fail to pay fees when due, or
          if we are required to do so by law.
        </p>
        <p>
          Upon termination, your right to use the Services ceases. Sections that by their nature should survive (including
          intellectual property, disclaimers, limitation of liability, indemnification, and governing law) will survive.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>19. DMCA / copyright notices</h2>
        <p>
          We respect intellectual property rights. If you believe material on our Services infringes your copyright, you may send a
          notice with the information typically required under applicable law (such as the U.S. Digital Millennium Copyright Act),
          including a description of the work, the location of the material, your contact details, and statements of good faith and
          accuracy. We may remove or disable access to material we deem appropriate following review.
        </p>
        <p>
          Designated copyright notices may be submitted through our <Link to='/support' className='text-yellow-400 underline hover:text-yellow-300'>Support</Link>{' '}
          channel; we will publish a dedicated legal contact if we establish one for production.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>20. Governing law and disputes</h2>
        <p>
          These Terms are governed by the laws of the State of Delaware, USA, excluding conflict-of-law rules, unless a different
          governing law is specified in a signed order form with you. You and we consent to the exclusive jurisdiction and venue of
          the state and federal courts located in Delaware for disputes arising out of these Terms or the Services, subject to
          mandatory consumer protections in your jurisdiction where they may not be waived.
        </p>
        <p>
          Before filing a claim, you agree to try to resolve the dispute informally by contacting us through{' '}
          <Link to='/support' className='text-yellow-400 underline hover:text-yellow-300'>Support</Link>.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>21. General</h2>
        <ul className='list-disc space-y-2 pl-6'>
          <li>
            <span className='font-medium text-white'>Entire agreement:</span> These Terms, together with the Privacy Policy, Cookie
            Policy, and any order form or enterprise agreement, constitute the entire agreement regarding the Services and supersede
            prior oral or written understandings on the same subject.
          </li>
          <li>
            <span className='font-medium text-white'>Assignment:</span> You may not assign these Terms without our consent. We may
            assign them in connection with a merger, acquisition, or sale of assets.
          </li>
          <li>
            <span className='font-medium text-white'>Severability:</span> If a provision is unenforceable, the remaining provisions
            remain in effect.
          </li>
          <li>
            <span className='font-medium text-white'>Waiver:</span> Failure to enforce a provision is not a waiver of future
            enforcement.
          </li>
          <li>
            <span className='font-medium text-white'>Force majeure:</span> We are not liable for delays or failures due to events
            beyond our reasonable control.
          </li>
        </ul>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>22. Contact</h2>
        <p>
          Questions about these Terms: use our <Link to='/support' className='text-yellow-400 underline hover:text-yellow-300'>Support</Link>{' '}
          page. For privacy matters, see our <Link to='/privacy' className='text-yellow-400 underline hover:text-yellow-300'>Privacy Policy</Link>.
        </p>
      </section>
    </PublicPageLayout>
  );
}
